// Shrinks a photo in the browser before upload, so drivers on mobile data
// don't wait on multi-MB camera files.
const MAX_DIMENSION = 1600; // px, longest side
const QUALITY = 0.8; // JPEG quality
const SKIP_BELOW_BYTES = 300 * 1024; // already small enough

async function loadImage(file) {
  // createImageBitmap applies the EXIF orientation, so portrait photos stay upright
  if (window.createImageBitmap) {
    try {
      return await createImageBitmap(file, { imageOrientation: "from-image" });
    } catch {
      // fall through to <img>
    }
  }
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.src = url;
    await img.decode();
    return img;
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Returns a JPEG File at most MAX_DIMENSION px on its longest side.
 * Falls back to the original file if it's already small, can't be decoded,
 * or compressing wouldn't make it smaller.
 */
export async function compressImage(file) {
  if (!file || file.size <= SKIP_BELOW_BYTES) return file;

  try {
    const image = await loadImage(file);
    const width = image.width;
    const height = image.height;
    const scale = Math.min(1, MAX_DIMENSION / Math.max(width, height));

    const canvas = document.createElement("canvas");
    canvas.width = Math.round(width * scale);
    canvas.height = Math.round(height * scale);
    const ctx = canvas.getContext("2d");
    // JPEG has no transparency; keep PNG cut-outs white instead of black
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
    image.close?.();

    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", QUALITY)
    );
    if (!blob || blob.size >= file.size) return file;

    const name = file.name.replace(/\.[^.]+$/, "") + ".jpg";
    return new File([blob], name, { type: "image/jpeg", lastModified: Date.now() });
  } catch (error) {
    console.warn("Photo compression failed, uploading original:", error);
    return file;
  }
}
